import { getLocalStorage, setLocalStorage } from '@/utils/localStrage';
import { LocalStorageKeys } from '@/constants/localStorage';
import { apiPostRequest } from '@/utils/apiClient';
import { ActivityCalendarDataList, ActivityDataForLs } from '@/types/ActivityCalendarType';
import { ActivityCalendarData } from '@/types/ActivityCalendarType';

/**
 * カレンダーデータをローカルストレージから取得
 */
export async function getActivityInfoFromLs(userId: string): Promise<ActivityDataForLs | null> {
    // 初期値
    let result: ActivityDataForLs | null = null;
    // ローカルストレージからデータ取得
    const localData = await getLocalStorage(userId, LocalStorageKeys.ACTIVITY_CALENDAR_INFO);
    // データがある場合
    if (localData) {
        try {
            // データをパース
            result = JSON.parse(localData) as ActivityDataForLs;
        } catch (e) {
            // パースエラー時は null を返却
            result = null;
        }
    }
    return result;
}

/**
 * カレンダーのデータをDBから取得し、ローカルストレージに保存したうえで返却
 */
export async function getActivityInfoFromDb(
    userId: string,
): Promise<ActivityCalendarDataList | null> {
    try {
        // データベースから取得
        const response = await apiPostRequest('/api/activity-calendar/get', {});
        // 失敗時は null
        if (!response?.ok) {
            return null;
        }

        // JSON を取得
        const dbData = (await response.json()) as ActivityCalendarDataList;

        // ローカルストレージに登録（データはJSON化）
        const activityDataForLs: ActivityDataForLs = {
            updateDate: new Date().toISOString(),
            activities: dbData.activityCalendarData,
        };
        await setLocalStorage(
            userId,
            LocalStorageKeys.ACTIVITY_CALENDAR_INFO,
            JSON.stringify(activityDataForLs),
        );

        return dbData;
    } catch (error) {
        return null;
    }
}

/**
 * 画面で扱うアクティビティセット型
 */
export interface ActivitySet {
    displayName: string;
    searchKeyword: string;
    color?: string;
    dates: Date[];
    timeMap: Record<string, number>; // YYYY-MM-DD -> total minutes
}

/**
 * API から取得したデータを ActivitySet[] に変換
 */
export function convertApiDataToActivitySets(data: ActivityCalendarData[]): ActivitySet[] {
    return (
        data
            // ソート
            .sort((a, b) => a.sortOrder - b.sortOrder)
            // 型に合わせる
            .map((item) => {
                const timeMap: Record<string, number> = {};
                item.dateAndTimeList.forEach((dt) => {
                    timeMap[dt.date] = dt.totalTime;
                });
                // 'yyyy/MM/dd' を安全に日付へ変換（ブラウザの不定なDateパースを回避）
                const toLocalDate = (s: string): Date => {
                    const [y, m, d] = s.split(/[\/-]/).map((v) => parseInt(v, 10));
                    return new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
                };

                return {
                    // 表示名
                    displayName: item.displayName,
                    // 検索キーワード
                    searchKeyword: item.searchKeyword,
                    // 色（任意）
                    color: item.color || '',
                    // 日付（ローカルタイムでの0:00に固定）
                    dates: Object.keys(timeMap).map((d) => toLocalDate(d)),
                    // 時間
                    timeMap,
                } as ActivitySet;
            })
    );
}

/**
 * 空データを除外
 */
export function filterVisibleActivities(list: ActivitySet[]): ActivitySet[] {
    return list.filter(
        (a) =>
            a.displayName.trim() !== '' &&
            (a.searchKeyword.trim() !== '' || (a.color || '').trim() !== ''),
    );
}

/**
 * キーワードフォームのバリデーション結果
 */
export type KeywordValidationErrorKey =
    | 'displayNameRequired'
    | 'keywordOrColorRequired'
    | 'maxLengthExceeded';

export interface KeywordValidationResult {
    valid: boolean;
    errorKey?: KeywordValidationErrorKey;
    keywordsToRegister?: {
        sortOrder: number;
        displayName: string;
        searchKeyword: string;
        color?: string;
    }[];
}

/**
 * フォーム入力検証 & 登録用配列作成（アクティビティ用）
 *   - いずれかの入力（表示名/キーワード/色）がある場合は、
 *     表示名は必須 かつ （検索キーワード もしくは 色が「指定なし」以外）が必須
 *   - 全て空なら、その行は空行として登録（displayName/key/color共に空）
 *   - 表示名・検索キーワードは最大16文字
 * @param keywordForm フォーム state
 * @param maxItems チェック対象数
 */
export function validateKeywordForm(
    keywordForm: { displayName: string; searchKeyword: string; color?: string }[],
    maxItems: number,
): KeywordValidationResult {
    const keywordsToRegister: {
        sortOrder: number;
        displayName: string;
        searchKeyword: string;
        color?: string;
    }[] = [];

    for (let i = 0; i < maxItems; i++) {
        const { displayName = '', searchKeyword = '', color = '' } = keywordForm[i] || ({} as any);
        const disp = displayName.trim();
        const key = searchKeyword.trim();
        // 'all' は意味を持たないため、空文字に正規化
        const normalizedColor = (color || '') === 'all' ? '' : color || '';

        const allEmpty = disp === '' && key === '' && normalizedColor === '';
        const hasAnyInput = !allEmpty;

        // いずれか入力がある場合: 表示名必須
        if (hasAnyInput && disp === '') {
            return { valid: false, errorKey: 'displayNameRequired' };
        }
        // いずれか入力がある場合: （キーワード or 色≠指定なし）
        if (hasAnyInput && key === '' && normalizedColor === '') {
            return { valid: false, errorKey: 'keywordOrColorRequired' };
        }

        // 文字数チェック（両方入力されている/どちらか入力されている場合）
        if (disp.length > 16 || key.length > 16) {
            return {
                valid: false,
                errorKey: 'maxLengthExceeded',
            };
        }

        keywordsToRegister.push({
            sortOrder: i + 1,
            displayName: disp,
            searchKeyword: key,
            color: normalizedColor,
        });
    }

    return { valid: true, keywordsToRegister };
}
